import {createContext, FC, useCallback, useContext, useMemo, useState} from 'react'
import {WithChildren} from '../../../_metronic/helpers'
import {
  canonicalizeModuleCode,
  getModuleDefinition,
  ModuleCode,
  ModuleDefinition,
} from './moduleRegistry'
import {
  clearSessionModuleCode,
  getRuntimeModuleCode,
  setSessionModuleCode,
} from './moduleSession'

export type ModuleContextValue = {
  moduleCode?: ModuleCode
  module?: ModuleDefinition
  defaultRoute: string
  isModuleSession: boolean
  activateModule: (moduleCode: string) => ModuleCode | undefined
  clearModule: () => void
}

const LEGACY_DEFAULT_ROUTE = '/dashboard'

const ModuleContext = createContext<ModuleContextValue>({
  defaultRoute: LEGACY_DEFAULT_ROUTE,
  isModuleSession: false,
  activateModule: () => undefined,
  clearModule: () => {},
})

export const ModuleProvider: FC<WithChildren> = ({children}) => {
  const [moduleCode, setModuleCode] = useState<ModuleCode | undefined>(() => getRuntimeModuleCode())

  const activateModule = useCallback((value: string) => {
    const code = canonicalizeModuleCode(value)
    if (!code) {
      return undefined
    }

    setSessionModuleCode(code)
    setModuleCode(code)
    return code
  }, [])

  const clearModule = useCallback(() => {
    clearSessionModuleCode()
    setModuleCode(getRuntimeModuleCode())
  }, [])

  const value = useMemo<ModuleContextValue>(() => {
    const module = getModuleDefinition(moduleCode)
    return {
      moduleCode,
      module,
      defaultRoute: module?.defaultRoute || LEGACY_DEFAULT_ROUTE,
      isModuleSession: Boolean(moduleCode),
      activateModule,
      clearModule,
    }
  }, [activateModule, clearModule, moduleCode])

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
}

export function useModule() {
  return useContext(ModuleContext)
}
